import React, { useState } from 'react';
import {
  AlertCircle,
  Bot,
  CalendarClock,
  ExternalLink,
  Link2,
  Loader2,
  MapPin,
  Mic,
  RefreshCw,
  Users,
  Video,
} from 'lucide-react';
import type { CalendarEventDTO } from '../../global';
import { useCalendar } from './useCalendar';
import { useAutoJoin } from './useAutoJoin';
import { BOT_TONE_CLASS, botStatusLabel } from '../bot/botStatus';
import { Switch } from '../../components/ui/Switch';
import { dateLocale, t } from '../../lib/i18n';

interface CalendarCardProps {
  isDarkMode: boolean;
  /**
   * Etkinlik için yalnızca sistem sesini (karşı tarafı) kaydeder, bot
   * göndermez — başlık önden doldurulur.
   */
  onRecordSystemAudio?: (event: CalendarEventDTO) => void;
  /** Bot düğmesi kaydedilemediğinde (ağ vb.) kullanıcıya bildirim. */
  onError?: (message: string) => void;
}

function formatEventTime(event: CalendarEventDTO): string {
  if (event.isAllDay) return t('Tüm gün', 'All day');
  const start = new Date(event.startISO);
  const end = new Date(event.endISO);
  const fmt = (d: Date) => d.toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit' });
  return `${fmt(start)} – ${fmt(end)}`;
}

/** Etkinlik bugün değilse gün adını da göster ("Yarın 14:00" gibi). */
function formatEventDay(event: CalendarEventDTO): string | null {
  const start = new Date(event.startISO);
  const today = new Date();
  const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);

  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(start, today)) return null;
  if (sameDay(start, tomorrow)) return t('Yarın', 'Tomorrow');
  return start.toLocaleDateString(dateLocale(), { day: 'numeric', month: 'long' });
}

function isEventNow(event: CalendarEventDTO): boolean {
  if (event.isAllDay) return false;
  const now = Date.now();
  return now >= new Date(event.startISO).getTime() && now <= new Date(event.endISO).getTime();
}

/** Toplantı yakında mı (15 dk içinde) — "Katıl" düğmesini öne çıkarmak için. */
function isStartingSoon(event: CalendarEventDTO): boolean {
  if (event.isAllDay) return false;
  const diff = new Date(event.startISO).getTime() - Date.now();
  return diff > 0 && diff <= 15 * 60 * 1000;
}

export const CalendarCard: React.FC<CalendarCardProps> = ({ isDarkMode, onRecordSystemAudio, onError }) => {
  const calendar = useCalendar();
  const autoJoin = useAutoJoin();
  const [busyEventId, setBusyEventId] = useState<string | null>(null);

  const reportError = (err: unknown) =>
    onError?.((err as Error)?.message || t('Tercih kaydedilemedi.', 'Could not save your preference.'));

  const toggleMaster = (enabled: boolean) => {
    autoJoin.setEnabled(enabled).catch(reportError);
  };

  const toggleEvent = async (event: CalendarEventDTO, join: boolean) => {
    setBusyEventId(event.id);
    try {
      await autoJoin.setEventJoin(event, join);
    } catch (err) {
      reportError(err);
    } finally {
      setBusyEventId(null);
    }
  };

  /** Mikrofon düğmesi: kayıt hemen başlar (web'de paylaşım penceresi tıklamanın içinde açılmalı), bot bu toplantıya gitmez. */
  const recordSystemAudio = (event: CalendarEventDTO) => {
    onRecordSystemAudio?.(event);
    const state = autoJoin.stateFor(event);
    if (state.kind === 'toggle' && state.on) void toggleEvent(event, false);
  };

  // Bir bağlantıyı ana sürece açtırıyoruz — renderer'ın kabuğa doğrudan
  // erişimi yok, `openExternal` yalnızca http/https kabul ediyor.
  const openLink = (url: string) => {
    if (!url) return;
    void window.zabit.openExternal(url).catch(() => {});
  };

  return (
    <div
      id="card-up-next-schedule"
      className={`rounded-2xl border p-6 transition-all shadow-xs ${
        isDarkMode
          ? 'bg-[#19191C] border-[#3F3F42] text-neutral-200 shadow-black/20'
          : 'bg-white border-neutral-200/90 text-neutral-800 shadow-neutral-100'
      }`}
    >
      {/* Başlık satırı: tarih + bağlantı durumu */}
      <div className="text-[13px] font-semibold text-neutral-400 mb-4 pb-2.5 border-b border-inherit flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <span className="tracking-wide text-neutral-300 font-medium whitespace-nowrap">
          {new Date().toLocaleDateString(dateLocale(), {
            day: 'numeric',
            month: 'long',
            weekday: 'long',
          })}
        </span>

        {(calendar.status === 'idle' || calendar.status === 'not_connected') && (
          <button
            onClick={() => void calendar.connect()}
            className="text-[12px] font-semibold flex items-center gap-1.5 text-[#3AC8E0] hover:text-[#46CBE2] transition-colors"
          >
            <Link2 className="w-3.5 h-3.5" />
            {t('Google Calendar\'a Bağlan', 'Connect Google Calendar')}
          </button>
        )}

        {calendar.status === 'connecting' && (
          <span className="text-[12px] text-neutral-400 font-normal flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            {t('Bağlanıyor... tarayıcınızı kontrol edin', 'Connecting… check your browser')}
          </span>
        )}

        {(calendar.status === 'ready' || calendar.status === 'loading') && (
          <span className="text-[12px] text-neutral-400 font-normal flex items-center gap-2 whitespace-nowrap">
            {/* Ana düğme: genel otomatik bot ayarı. Toplantılarda elle yapılan seçimlere dokunmaz. */}
            <span
              className="flex items-center gap-2"
              title={t(
                'Açıkken bot, elle kapatmadığın her toplantıya katılır. Toplantıların kendi düğmeleri bundan etkilenmez.',
                'When on, the bot joins every meeting you haven’t switched off. Each meeting’s own switch is left as is.'
              )}
            >
              <Bot className="w-3.5 h-3.5 text-[#46CBE2]" />
              <span className="text-neutral-300 font-medium">{t('Otomatik bot', 'Auto bot')}</span>
              <Switch
                checked={autoJoin.enabled}
                disabled={!autoJoin.loaded}
                onChange={toggleMaster}
                label={t('Otomatik bot katılımı', 'Automatic bot joining')}
              />
            </span>
            <button
              onClick={() => void calendar.refresh()}
              className="text-neutral-500 hover:text-[#46CBE2] transition-colors p-0.5"
              title={t('Yenile', 'Refresh')}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${calendar.status === 'loading' ? 'animate-spin' : ''}`} />
            </button>
          </span>
        )}

        {calendar.status === 'auth_expired' && (
          <button
            onClick={() => void calendar.connect()}
            className="text-[12px] font-semibold flex items-center gap-1.5 text-amber-400 hover:text-amber-300 transition-colors"
          >
            <AlertCircle className="w-3.5 h-3.5" />
            {t('Bağlantı süresi doldu — Yeniden Bağlan', 'Access expired — reconnect')}
          </button>
        )}

        {calendar.status === 'error' && (
          <button
            onClick={() => void calendar.refresh()}
            className="text-[12px] text-rose-400 font-normal flex items-center gap-1.5 hover:text-rose-300"
          >
            <AlertCircle className="w-3.5 h-3.5" />
            {calendar.message} {t('Tekrar dene', 'Try again')}
          </button>
        )}
      </div>

      <div className="space-y-2">
        {(calendar.status === 'idle' || calendar.status === 'not_connected') && (
          <div className="py-2 space-y-1.5">
            <p className="text-[13px] text-neutral-500">
              {t('Yaklaşan toplantılarınızı burada görmek için takviminizi bağlayın.', 'Connect your calendar to see your upcoming meetings here.')}
            </p>
            {calendar.connectError && (
              <p className="text-[12px] text-rose-400 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                {t('Bağlantı başarısız oldu:', 'Connection failed:')} {calendar.connectError}
              </p>
            )}
          </div>
        )}

        {calendar.status === 'loading' && calendar.events.length === 0 && (
          <p className="text-[13px] text-neutral-500 py-2 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            {t('Yükleniyor...', 'Loading…')}
          </p>
        )}

        {calendar.status === 'ready' && calendar.events.length === 0 && (
          /* Gün sayısı ayarlardan geliyor; burada sabit "7 gün" yazmak,
             yönetici pencereyi değiştirdiğinde yanlış bilgi veriyordu. */
          <div className="py-2 space-y-1">
            <p className="text-[13px] text-neutral-500">{t('Bugün için toplantı yok.', 'No meetings today.')}</p>
            <p className="text-[12px] text-neutral-600">
              {t('Üstteki', 'Use')} <span className="text-neutral-400 font-medium">{t('Kayıt Al', 'Record')}</span>{' '}
              {t('ile yeni bir kayıt başlatabilirsiniz.', 'above to start a new recording.')}
            </p>
          </div>
        )}

        {calendar.status === 'auth_expired' && (
          <p className="text-[13px] text-neutral-500 py-2">
            {t(
              'Google Calendar bağlantınızın süresi doldu — yaklaşan toplantılarınızı tekrar görmek için üstteki düğmeyle yeniden bağlanın.',
              'Your Google Calendar access has expired — use the button above to reconnect and see your upcoming meetings again.'
            )}
          </p>
        )}

        {calendar.events.map((event) => {
          const now = isEventNow(event);
          const soon = isStartingSoon(event);
          const dayLabel = formatEventDay(event);
          const highlight = now || soon;

          return (
            <div
              key={event.id}
              className={`flex items-start gap-4 p-2.5 rounded-xl transition-all group ${
                highlight
                  ? `${isDarkMode ? 'bg-[#19191C]' : 'bg-neutral-50'} border ${
                      now ? 'border-emerald-500/20' : 'border-amber-500/20'
                    }`
                  : 'border border-transparent'
              }`}
            >
              <div
                className={`w-1 self-stretch rounded-full shrink-0 ${
                  now
                    ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]'
                    : soon
                      ? 'bg-amber-500'
                      : 'bg-neutral-500'
                }`}
              ></div>

              <div className="min-w-0 flex-1">
                {/* Dar ekranda (telefon) düğmeler başlığın altına iner. */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
                  <div className="min-w-0">
                    {/* Başlığa tıklamak etkinliği Google Takvim'de açar. */}
                    <button
                      onClick={() => openLink(event.htmlLink)}
                      disabled={!event.htmlLink}
                      title={event.htmlLink ? t('Google Takvim\'de aç', 'Open in Google Calendar') : undefined}
                      className="flex items-center gap-2 text-[14.5px] font-semibold text-neutral-200 text-left disabled:cursor-default enabled:hover:text-[#46CBE2] transition-colors"
                    >
                      <span className="truncate">{event.title}</span>
                      {event.htmlLink && (
                        <ExternalLink className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-60 transition-opacity" />
                      )}
                    </button>

                    <div className="text-[13px] text-neutral-400 mt-1 flex items-center flex-wrap gap-x-2.5 gap-y-1">
                      <span className="font-mono">
                        {dayLabel ? `${dayLabel} · ` : ''}
                        {formatEventTime(event)}
                      </span>

                      {typeof event.attendeeCount === 'number' && event.attendeeCount > 1 && (
                        <span className="flex items-center gap-1 text-neutral-500">
                          <Users className="w-3 h-3" />
                          {event.attendeeCount}
                        </span>
                      )}

                      {/* Video linki yoksa fiziksel konumu göster. */}
                      {!event.conference && event.location && (
                        <span className="flex items-center gap-1 text-neutral-500 truncate max-w-[200px]">
                          <MapPin className="w-3 h-3 shrink-0" />
                          <span className="truncate">{event.location}</span>
                        </span>
                      )}

                      {now && (
                        <span className="text-[11px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                          {t('Şimdi', 'Now')}
                        </span>
                      )}
                      {!now && soon && (
                        <span className="text-[11px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                          {t('Birazdan', 'Soon')}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                    {/* Yalnızca sistem sesini kaydet — bot gönderilmez, başlık önden dolar. */}
                    {onRecordSystemAudio && (
                      <button
                        onClick={() => recordSystemAudio(event)}
                        title={t(
                          'Bu toplantıyı bilgisayarın sesinden kaydet (yalnızca sistem sesi, bot gönderilmez)',
                          'Record this meeting from your computer’s audio (system audio only, no bot)'
                        )}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-semibold transition-all active:scale-95 ${
                          highlight
                            ? 'bg-rose-600/90 hover:bg-rose-500 text-[#F0EEE6]'
                            : `text-neutral-500 hover:text-rose-400 ${isDarkMode ? 'hover:bg-rose-500/10' : 'hover:bg-rose-50'}`
                        }`}
                      >
                        <Mic className="w-3.5 h-3.5" />
                        {highlight && <span>{t('Kaydet', 'Record')}</span>}
                      </button>
                    )}

                    {/* Bot düğmesi — yalnızca Meet/Zoom/Teams linki olan etkinliklerde. */}
                    {(() => {
                      const state = autoJoin.stateFor(event);
                      if (state.kind === 'none') return null;
                      if (state.kind === 'status') {
                        const { label, tone } = botStatusLabel({ status: state.status, meetingUrl: '' });
                        return (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] font-semibold ${BOT_TONE_CLASS[tone]}`}>
                            <Bot className="w-3 h-3" />
                            {label}
                          </span>
                        );
                      }
                      return (
                        <span
                          className="flex items-center gap-1.5 px-1.5"
                          title={
                            state.on
                              ? t('Bot bu toplantıya katılacak — kapatırsan katılmaz', 'The bot will join this meeting — switch off to skip it')
                              : t('Bot bu toplantıya katılmayacak — açarsan katılır', 'The bot won’t join this meeting — switch on to send it')
                          }
                        >
                          {busyEventId === event.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-neutral-500" />
                          ) : (
                            <Bot className={`w-3.5 h-3.5 ${state.on ? 'text-[#46CBE2]' : 'text-neutral-500'}`} />
                          )}
                          <Switch
                            checked={state.on}
                            disabled={busyEventId === event.id}
                            onChange={(join) => void toggleEvent(event, join)}
                            label={t(`Bot "${event.title}" toplantısına katılsın`, `Bot joins "${event.title}"`)}
                          />
                        </span>
                      );
                    })()}

                    {/* "Katıl" — toplantının video bağlantısını tarayıcıda açar. */}
                    {event.conference && (
                      <button
                        onClick={() => openLink(event.conference!.url)}
                        title={t(`${event.conference.label} ile katıl`, `Join with ${event.conference.label}`)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all active:scale-95 ${
                          highlight
                            ? 'bg-[#1DA4BA] hover:bg-[#3AC8E0] text-[#F0EEE6] shadow-xs'
                            : isDarkMode
                              ? 'bg-[#1F1F23] hover:bg-[#26262A] text-neutral-300 hover:text-[#F0EEE6]'
                              : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
                        }`}
                      >
                        <Video className="w-3.5 h-3.5" />
                        <span>{t('Katıl', 'Join')}</span>
                      </button>
                    )}
                  </div>

                  {/* Video linki de konum da yoksa en azından takvime götür. */}
                  {!event.conference && !event.location && event.htmlLink && (
                    <button
                      onClick={() => openLink(event.htmlLink)}
                      title={t('Google Takvim\'de aç', 'Open in Google Calendar')}
                      className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-medium text-neutral-500 hover:text-[#46CBE2] opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <CalendarClock className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
